//
//  pageTours.ts
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module builds each page's onboarding tour from its page guide.
//

import helpPages from "./helpPages";
import {
    cardTourTarget,
    columnTourTarget,
    fieldTourTarget,
    helpStep,
    helpTourTargets,
    menuTourTarget,
    pageTitleStep,
    type HelpTourStep
} from "./helpTourTargets";
import pageGuides from "./pageGuides";
import {
    chapterOf,
    tourChapters,
    type PageGuide,
    type PageGuideStop
} from "./pageGuides/pageGuide";

/** Converts one guide stop into the tour steps it visits, each in the stop's chapter. */
const toSteps = (guideStop: PageGuideStop): HelpTourStep[] => {
    const chapter = chapterOf(guideStop);

    switch (guideStop.kind) {
        case "field":
            return [
                {
                    id: fieldTourTarget(guideStop.path),
                    title: guideStop.label,
                    content: guideStop.text,
                    permission: guideStop.permission,
                    chapter
                }
            ];
        case "grid":
            return [
                {
                    id: helpTourTargets.gridRow,
                    title: guideStop.title,
                    content: guideStop.text,
                    chapter
                },
                ...(guideStop.columns ?? []).map(item => ({
                    id: columnTourTarget(item.header),
                    title: `Column: ${item.header}`,
                    content: item.text,
                    chapter
                }))
            ];
        case "menu":
            return guideStop.items.map(item => ({
                id: menuTourTarget(item.label),
                title: item.label,
                content: item.text,
                chapter
            }));
        case "cards":
            return guideStop.items.map(item => ({
                id: cardTourTarget(item.label),
                title: item.label,
                content: item.text,
                chapter
            }));
        default:
            return [
                {
                    id: guideStop.target,
                    title: guideStop.title,
                    content: guideStop.text,
                    permission: guideStop.permission,
                    chapter
                }
            ];
    }
};

/**
 * Keys each step by its target id, numbering a target's later steps (`page-actions#2`) so every
 * key in the tour is unique and its stored text can be found.
 */
const withStepKeys = (steps: HelpTourStep[]): HelpTourStep[] => {
    const seen = new Map<string, number>();
    return steps.map(step => {
        const count = (seen.get(step.id) ?? 0) + 1;
        seen.set(step.id, count);
        return { ...step, key: count === 1 ? step.id : `${step.id}#${count}` };
    });
};

/**
 * Builds a page's tour: its introduction on the page heading, each guide stop in order, then help.
 *
 * Postconditions: Every step but the closing help step carries its chapter, so a chapter can be
 * toured alone. Steps whose target the page does not display, or whose permission the user lacks
 * at the page context, are dropped when the tour starts.
 */
export const buildGuideTour = (title: string, guide: PageGuide): HelpTourStep[] =>
    withStepKeys([
        { ...pageTitleStep(title, guide.intro), chapter: tourChapters.overview },
        ...guide.stops.flatMap(toSteps),
        helpStep
    ]);

/**
 * Tours keyed by the page's help route (see helpPages). Every page with a guide has a tour; pages
 * sharing another page's help also share its tour.
 */
const pageTours: Record<string, readonly HelpTourStep[]> = Object.fromEntries(
    Object.entries(pageGuides).map(([route, guide]) => [
        route,
        buildGuideTour(helpPages[route]?.title ?? "This page", guide)
    ])
);

export default pageTours;
